
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import ButtonGroup from '@/components/automated-scenarios/fields/ButtonGroup.vue';
import Dropdown from 'primevue/dropdown';
import {
  defineComponent,
  reactive, ref, watch, computed,
} from 'vue';
import { GlobalSettingsData, Period, DateRangeWorkflow } from '@/types/automated-scenarios';
import { asInt, showToastError } from '@/helpers';
import PeriodSelector from '@/components/automated-scenarios/fields/PeriodSelector.vue';
import {
  AutomatedScenarioState as state,
  setGlobalSettingsData,
} from '@/composables/AutomatedScenarios';
import Button from 'primevue/button';
import Card from 'primevue/card';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { CampaignAutomationTypeEnum } from '@/types';

export default defineComponent({
  name: 'SettingsPanelAutomation',

  components: {
    FieldErrors,
    PeriodSelector,
    InputText,
    ButtonGroup,
    Button,
    Card,
    Dropdown,
  },

  props: {
    settingPanelValidationErrors: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  setup(props: {settingPanelValidationErrors: any }) {
    const { t } = useI18n();
    const formValidationErrors = ref({});
    const componentFieldErrorsKey = ref(0);
    watch(() => props.settingPanelValidationErrors, () => {
      formValidationErrors.value = props.settingPanelValidationErrors;
      componentFieldErrorsKey.value += 1;
    });

    const settingsFields: GlobalSettingsData = reactive({
      name: (state.scenario.settingsData as GlobalSettingsData).name,
      type_workflow: (state.scenario.settingsData as GlobalSettingsData).type_workflow,
      unique: (state.scenario.settingsData as GlobalSettingsData).unique,
      type_unique: (state.scenario.settingsData as GlobalSettingsData).type_unique,
      type_retrigger: (state.scenario.settingsData as GlobalSettingsData).type_retrigger,
      time_unit_select: (state.scenario.settingsData as GlobalSettingsData).time_unit_select,
      program_date_ranges: (state.scenario.settingsData as GlobalSettingsData).program_date_ranges,
      date_ranges: (state.scenario.settingsData as GlobalSettingsData).date_ranges,
      // v2 schedule passthrough (not editable here) — kept so the settings
      // write-back never drops a schedule configured in the new editor.
      schedule_config: (state.scenario.settingsData as GlobalSettingsData).schedule_config,
    });

    const optionsFields = {
      type_workflow: [
        { value: CampaignAutomationTypeEnum.MARKETING, label: t('automatedScenarios.globalSettings.type_workflow.options.marketing') },
        { value: CampaignAutomationTypeEnum.TRANSACTIONAL, label: t('automatedScenarios.globalSettings.type_workflow.options.transactional') },
      ],

      unique: [
        { value: 1, label: t('yes') },
        { value: 0, label: t('no') },
      ],

      type_unique: [
        { value: 1, label: t('automatedScenarios.globalSettings.type_unique.options.visitor') },
        { value: 0, label: t('automatedScenarios.globalSettings.type_unique.options.session') },
      ],

      type_retrigger: [
        { value: 1, label: t('yes') },
        { value: 0, label: t('no') },
      ],

      program_date_ranges: [
        { value: 1, label: t('yes') },
        { value: 0, label: t('no') },
      ],
    };

    const units_retrigger = [
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
      { value: 'MONTH', label: t('automatedScenarios.fields.durations.month(s)') },
    ];

    const period_retrigger = ref<Period>({
      unit: settingsFields.time_unit_select.unit,
      value: asInt(settingsFields.time_unit_select.value),
    });

    const dateRanges: DateRangeWorkflow[] = reactive(JSON.parse(settingsFields.date_ranges || '[]'));

    const startDay = ref<number | null>(null);
    const startMonth = ref<number | null>(null);
    const endDay = ref<number | null>(null);
    const endMonth = ref<number | null>(null);
    const yearOption = ref<string>('current');
    const startYear = ref<number | null>(null);
    const endYear = ref<number | null>(null);
    const inclusionOption = ref<string>('include');
    const buttonGroupKey = ref<number>(0);

    const dayOptions = computed(() => {
      const days = [];
      for (let i = 1; i <= 31; i++) {
        days.push({ label: i.toString(), value: i });
      }
      return days;
    });

    const monthOptions = computed(() => [
      { label: t('months.january'), value: 1 },
      { label: t('months.february'), value: 2 },
      { label: t('months.march'), value: 3 },
      { label: t('months.april'), value: 4 },
      { label: t('months.may'), value: 5 },
      { label: t('months.june'), value: 6 },
      { label: t('months.july'), value: 7 },
      { label: t('months.august'), value: 8 },
      { label: t('months.september'), value: 9 },
      { label: t('months.october'), value: 10 },
      { label: t('months.november'), value: 11 },
      { label: t('months.december'), value: 12 },
    ]);

    const yearOptionChoices = computed(() => [
      { label: t('automatedScenarios.globalSettings.program_date_ranges.card.currentYear'), value: 'current' },
      { label: t('automatedScenarios.globalSettings.program_date_ranges.card.everyYear'), value: 'everyYear' },
    ]);

    const inclusionOptionChoices = computed(() => [
      { label: t('automatedScenarios.globalSettings.program_date_ranges.card.includePeriod'), value: 'include' },
      { label: t('automatedScenarios.globalSettings.program_date_ranges.card.excludePeriod'), value: 'exclude' },
    ]);

    const yearSelectionOptions = computed(() => {
      const current = new Date().getFullYear();
      const options = [];
      for (let i = 0; i < 10; i += 1) {
        options.push({ label: String(current + i), value: current + i });
      }
      return options;
    });

    watch(yearOption, (newValue) => {
      if (newValue === 'everyYear') {
        startYear.value = null;
        endYear.value = null;
      }
    });

    const addDateRange = () => {
      if (
        startDay.value
        && startMonth.value
        && endDay.value
        && endMonth.value
        && yearOption.value
        && (yearOption.value === 'everyYear' || (startYear.value && endYear.value))
        && inclusionOption.value
      ) {
        if (yearOption.value === 'everyYear' && startMonth.value === endMonth.value && startDay.value > endDay.value) {
          showToastError(t('automatedScenarios.globalSettings.program_date_ranges.card.error_end_day_before_start_day'));
          return;
        }

        let new_date_start_str: string;
        let new_date_end_str: string;

        if (yearOption.value === 'current') {
          if (!startYear.value || !endYear.value) {
            // This case should be prevented by the button's disabled state, but as a safeguard:
            showToastError('Year must be selected for current year option.');
            return;
          }
          const startDate = new Date(startYear.value, startMonth.value! - 1, startDay.value!);
          const endDate = new Date(endYear.value, endMonth.value! - 1, endDay.value!);

          if (endDate < startDate) {
            showToastError(t('automatedScenarios.globalSettings.program_date_ranges.card.error_end_date_before_start_date'));
            return;
          }
          new_date_start_str = `${startYear.value}-${String(startMonth.value).padStart(2, '0')}-${String(startDay.value).padStart(2, '0')}`;
          new_date_end_str = `${endYear.value}-${String(endMonth.value).padStart(2, '0')}-${String(endDay.value).padStart(2, '0')}`;
        } else { // everyYear
          new_date_start_str = `${String(startMonth.value).padStart(2, '0')}-${String(startDay.value).padStart(2, '0')}`;
          new_date_end_str = `${String(endMonth.value).padStart(2, '0')}-${String(endDay.value).padStart(2, '0')}`;
        }

        const found = dateRanges.find((element) => element.date_start === new_date_start_str
          && element.date_end === new_date_end_str
          && element.year_option === yearOption.value
          && element.inclusion_type === inclusionOption.value);

        if (found) {
          showToastError(t('automatedScenarios.globalSettings.program_date_ranges.card.error_duplicate_range'));
        } else {
          const newRange: DateRangeWorkflow = {
            date_start: new_date_start_str,
            date_end: new_date_end_str,
            year_option: yearOption.value,
            inclusion_type: inclusionOption.value,
          };
          dateRanges.push(newRange);

          startDay.value = null;
          startMonth.value = null;
          endDay.value = null;
          endMonth.value = null;

          if (yearOption.value === 'current') {
            startYear.value = null;
            endYear.value = null;
          }

          yearOption.value = 'current';
          inclusionOption.value = 'include';
          buttonGroupKey.value += 1;
        }
      }
    };

    const formatDateRangeForDisplay = (range: DateRangeWorkflow): string => {
      const safeDateStart = range.date_start || '';
      const safeDateEnd = range.date_end || '';

      const startParts = safeDateStart.split('-');
      const endParts = safeDateEnd.split('-');

      let startDateStr = '-';
      let endDateStr = '-';
      let formatKey = '';

      if (range.year_option === 'current') {
        formatKey = 'automatedScenarios.globalSettings.program_date_ranges.card.currentYearRangeFormat';
        // Expects YYYY-MM-DD, so 3 parts. Ensure all parts are non-empty.
        if (startParts.length === 3 && startParts.every((p) => p)) {
          startDateStr = `${startParts[2]}/${startParts[1]}/${startParts[0]}`;
        }

        if (endParts.length === 3 && endParts.every((p) => p)) {
          endDateStr = `${endParts[2]}/${endParts[1]}/${endParts[0]}`;
        }
      } else if (range.year_option === 'everyYear') {
        formatKey = 'automatedScenarios.globalSettings.program_date_ranges.card.everyYearRangeFormat';
        // Expects MM-DD, so 2 parts. Ensure all parts are non-empty.
        if (startParts.length === 2 && startParts.every((p) => p)) {
          startDateStr = `${startParts[1]}/${startParts[0]}`;
        }

        if (endParts.length === 2 && endParts.every((p) => p)) {
          endDateStr = `${endParts[1]}/${endParts[0]}`;
        }
      }

      let mainDisplay: string;
      if (formatKey) {
        mainDisplay = t(formatKey, { startDate: startDateStr, endDate: endDateStr });
      } else {
        mainDisplay = `${startDateStr} - ${endDateStr}`;
      }

      let iconHtml = '';
      if (range.inclusion_type === 'include') {
        iconHtml = '<i class="fas fa-check" style="margin-right: 8px; color: var(--green-500);"></i>';
      } else if (range.inclusion_type === 'exclude') {
        iconHtml = '<i class="fas fa-xmark" style="margin-right: 8px; color: var(--red-500);"></i>';
      }

      return iconHtml + mainDisplay;
    };

    const removeDateRange = (index: number) => {
      dateRanges.splice(index, 1);
    };

    watch([settingsFields, period_retrigger, dateRanges], () => {
      settingsFields.time_unit_select = period_retrigger.value;
      settingsFields.date_ranges = JSON.stringify(dateRanges);
      setGlobalSettingsData(settingsFields);
    });

    return {
      t,
      formValidationErrors,
      componentFieldErrorsKey,
      settingsFields,
      optionsFields,
      units_retrigger,
      period_retrigger,
      startDay,
      startMonth,
      endDay,
      endMonth,
      yearOption,
      startYear,
      endYear,
      inclusionOption,
      buttonGroupKey,
      dayOptions,
      monthOptions,
      yearOptionChoices,
      inclusionOptionChoices,
      yearSelectionOptions,
      dateRanges,
      addDateRange,
      removeDateRange,
      formatDateRangeForDisplay,
    };
  },
});
